/**
 * Celestia locale — ko-KR (Korean, primary) — board-game-client copy.
 *
 * UI is NOT shared between playground and client (CLAUDE.md). Key set
 * mirrors en-US.ts exactly; i18n.test.ts enforces this.
 *
 * Plural strings use ICU MessageFormat `{n, plural, ...}`. Korean has a
 * single CLDR plural category, so only `other` is provided (CLDR rule).
 *
 * Sources:
 *   - Error codes: 02-rules-spec.md §8
 *   - Phase names, UI strings: docs/wireframes/board-game-client/celestia.md
 */

export const koKR: Readonly<Record<string, string>> = {
  // --- Error codes (spec §8) ---
  ERR_STALE_VERSION: "오래된 상태입니다. 다시 시도하세요.",
  ERR_DUPLICATE_ACTION_ID: "이미 처리된 동작입니다.",
  ERR_PHASE_MISMATCH: "지금 단계에서는 이 동작을 할 수 없습니다.",
  ERR_ACTOR_NOT_ELIGIBLE: "지금은 당신의 차례가 아닙니다.",
  ERR_MODULE_DISABLED: "비활성화된 확장 동작입니다.",
  ERR_LONE_CAPTAIN_REQUIRED: "단독 선장 상황에서만 가능합니다.",
  ERR_CAPTAIN_REQUIRED: "선장만 할 수 있는 동작입니다.",
  ERR_PASSENGER_REQUIRED: "비행기에 탑승한 승객만 가능합니다.",
  ERR_NOT_ON_AIRCRAFT: "더 이상 탑승하고 있지 않습니다.",
  ERR_HAND_LACKS_CARD: "손에 없는 카드입니다.",
  ERR_PAYEQ_INCOMPLETE: "모든 이벤트 기호를 막지 못했습니다.",
  ERR_PAYEQ_OVERFULL: "필요한 것보다 많은 카드를 냈습니다.",
  ERR_PAYEQ_MUSTPLAY: "보유한 장비를 반드시 사용해야 합니다.",
  ERR_TURBO_OPTIONAL_ABUSE: "터보를 잘못된 방식으로 사용했습니다.",
  ERR_REROLL_DICE_INDEX: "다시 굴릴 수 없는 주사위입니다.",
  ERR_NO_BLANKS_TO_REROLL: "다시 굴릴 빈 주사위가 없습니다.",
  ERR_EJECT_SELF: "자신이나 선장은 사출할 수 없습니다.",
  ERR_DECK_EMPTY: "도시 보물 더미가 비었습니다.",
  ERR_TREASURE_CITY_MISMATCH: "보물을 받을 도시가 일치하지 않습니다.",
  ERR_SPYGLASS_NOT_HELD: "마법 망원경을 가지고 있지 않습니다.",
  ERR_JETPACK_NO_PROGRESS: "이번 여정에서 도달한 도시가 없습니다.",
  ERR_NOT_PENDING_EJECTOR: "사출 권한이 없습니다.",
  ERR_INVALID_STATE: "게임 상태가 올바르지 않습니다.",
  ERR_MISSING_ACTION_ID: "동작 식별자가 없습니다.",
  ERR_UNKNOWN_ACTION: "알 수 없는 동작입니다.",
  ERR_DECLARE_MUST_CAN:
    "장비로 모든 위험을 막을 수 있습니다 — 해결 가능을 선언해야 합니다.",
  ERR_DECLARE_MUST_CANNOT:
    "위험을 막을 수 없습니다 — 해결 불가를 선언해야 합니다.",

  // --- Client-side / network error fallbacks (multiplayer) ---
  ERR_NO_GAME: "진행 중인 게임이 없습니다.",
  ERR_INIT_FAILED: "게임 초기화에 실패했습니다.",
  ERR_STATE_FETCH_FAILED: "상태를 불러올 수 없습니다.",
  ERR_SUBMIT_FAILED: "동작 제출에 실패했습니다.",

  // --- Phase names (spec §5.1) ---
  phase_StartOfJourney: "여정 시작",
  phase_AwaitSoloCaptainChoice: "단독 선장 선택",
  phase_AwaitRoll: "주사위 굴리기",
  phase_AwaitCaptainClaim: "선장 공표",
  phase_AwaitPassengerDecision: "승객 결정",
  phase_AwaitEjectionWindow: "사출 단계",
  phase_AwaitCaptainDeclaration: "선장 선언",
  phase_AwaitReactionWindow: "반응 단계",
  phase_AwaitResolution: "장비 지불",
  phase_AwaitTreasureChoice: "보물 선택",
  phase_EndOfLeg: "구간 종료",
  phase_EndOfJourney: "여정 종료",

  // --- Button labels ---
  btn_start_game: "게임 시작",
  btn_roll_dice: "주사위 굴리기",
  btn_confirm_landing: "이 도시에 착륙",
  btn_stay: "탑승 유지",
  btn_leave: "하차 (보물 확보)",
  btn_declare_can: "해결 가능 선언",
  btn_declare_cannot: "해결 불가 선언",
  btn_declare_pass: "통과 선언",
  btn_declare_fail: "실패 선언",
  btn_jetpack: "제트팩",
  btn_alt_route: "주사위 다시 굴리기",
  btn_wind_gust: "돌풍",
  btn_spyglass: "마법 망원경",
  btn_pass: "패스",
  btn_pay_equipment: "장비 지불",
  btn_take_treasure: "보물 받기",
  btn_play_again: "새 게임",
  btn_back_to_lobby: "로비로",
  btn_close: "닫기",

  // --- UI strings ---
  ui_loading: "게임을 불러오는 중…",
  ui_waiting_first_move: "첫 동작을 기다리는 중…",
  ui_captain: "선장",
  ui_hand: "내 손패",
  ui_aboard: "탑승",
  ui_left: "하차",
  ui_treasures: "보물",
  ui_city: "도시",
  ui_shared_win: "공동 우승",
  ui_winner: "우승",
  ui_back: "로비로",
  ui_me: "나",

  // --- ICU plural strings (CLDR: ko other only) ---
  ui_hand_count: "내 손패: {n, plural, other {카드 #장}}",
  ui_passenger_count: "{n, plural, other {탑승객 #명}}",
  ui_treasure_count: "{n, plural, other {보물 #개}}",
  ui_handcount_label: "{n, plural, other {숨겨진 카드 #장}}",
  ui_remaining_treasure: "{n, plural, other {남은 보물 #개}}",
  ui_deadline_seconds: "{n, plural, other {#초 남음}}",

  // --- Die faces (spec §12) ---
  die_fog: "안개숲",
  die_lightning: "번개 폭풍",
  die_birds: "난폭한 새 떼",
  die_pirate: "해적",
  die_blank: "빈 면",
  die_fiftyFifty: "50-50",

  // --- Card labels (from rule schema, never from images — spec §3) ---
  card_eq_compass: "나침반 · 안개숲 무효",
  card_eq_arrester: "피뢰침 · 번개 폭풍 무효",
  card_eq_foghorn: "초강력 나팔 · 난폭한 새 떼 무효",
  card_eq_cannon: "대포 · 해적 무효",
  card_turbo: "터보 · 아무 기호 1개 대체",
  card_pw_ejection: "특수 · 추방",
  card_pw_jetpack: "특수 · 제트팩",
  card_pw_alternativeRoute: "특수 · 샛길",
  card_pw_windGust: "특수 · 돌풍",
  card_spyglass: "마법 망원경 · 미사용 시 +2",
  card_unknown: "카드",

  // --- Connection / reconnect (multiplayer-only) ---
  conn_connected: "연결됨",
  conn_reconnecting: "재연결 중…",
  conn_reconnecting_banner:
    "연결이 끊어졌습니다 — 재연결 중입니다. 게임은 안전합니다.",

  // --- Accessibility labels ---
  a11y_leave_game: "게임을 나가 로비로 돌아갑니다",
  a11y_phase_badge: "현재 단계: {phase}",
  a11y_deadline_ring: "남은 시간: {seconds}",
  a11y_seat_carousel: "좌석. 화살표 키로 좌석을 이동합니다.",
  a11y_seat: "{seat}번 좌석: {name}",
  a11y_card: "카드: {label}",
  a11y_die: "{n}번 주사위: {face} ({letter})",
  a11y_reaction_prompt: "반응 단계 열림: {phase}. 행동을 선택하세요.",
  a11y_aircraft_empty: "비행기: 비어 있음",
  a11y_aircraft_occupied: "비행기: 탑승 중",
  a11y_aircraft_crashing: "비행기: 추락 중",
  a11y_settings: "접근성 설정",
  a11y_loading: "게임을 불러오는 중입니다. 잠시 기다려 주세요",

  // --- Accessibility settings panel ---
  set_title: "접근성",
  set_dyslexia_font: "읽기 쉬운 글꼴",
  set_text_size: "글자 크기",
  set_text_normal: "기본",
  set_text_large: "크게 (+25%)",
  set_text_xlarge: "더 크게 (+50%)",

  // --- Display prefs bar (wireframe re-skin) ---
  a11y_settings_title: "디스플레이 옵션",
  a11y_toggle_language: "언어",
  a11y_toggle_dyslexia: "난독증 친화 글꼴",
  a11y_text_size: "글자 크기",
  a11y_text_normal: "보통",
  a11y_text_large: "크게 +25%",
  a11y_text_xlarge: "더 크게 +50%",

  // --- Seat lane (wireframe re-skin) ---
  seat_aboard: "탑승",
  seat_left: "하선",
  seat_treasure_short: "보물:{n}",
  a11y_seat_avatar: "{name}, {status}",
  a11y_seat_hand: "손패 {n}장",
  a11y_seat_treasure: "보물: {n}",

  // --- Phase record stack (wireframe re-skin) ---
  phase_record_done: "완료",
  phase_record_cur: "진행 중",
  phase_record_future: "예정",
  a11y_phase_stack: "단계 진행",

  // --- Waiting (multiplayer only — replaces PassDeviceGate) ---
  waiting_acting: "{name} 행동 중…",

  // --- Hand FAB ---
  a11y_hand_fab: "내 손패 · 점수",
  hand_panel_title: "내 손패 · 점수",
  hand_panel_close: "닫기",
  hand_panel_score_section: "내 점수 (보물 · 비공개)",
  hand_score_treasures: "확보한 보물",
  hand_score_total: "합계",

  // --- Hand tray (FAB card grid) ---
  a11y_hand: "내 손패",
  a11y_card_selected: "{label}, 선택됨",

  // --- Card schema text (rendered from rule schema, never baked into art) ---
  card_equip_compass_label: "나침반",
  card_equip_compass_footer: "안개숲 무효화",
  card_equip_arrester_label: "피뢰침",
  card_equip_arrester_footer: "번개 폭풍 무효화",
  card_equip_foghorn_label: "초강력 나팔",
  card_equip_foghorn_footer: "난폭한 새 떼 무효화",
  card_equip_cannon_label: "대포",
  card_equip_cannon_footer: "해적 무효화",
  card_turbo_label: "터보",
  card_turbo_footer: "심볼 1개 대체",
  card_power_ejection_label: "추방",
  card_power_jetpack_label: "제트팩",
  card_power_altroute_label: "샛길",
  card_power_windgust_label: "돌풍",
  card_power_footer: "파워 카드",
  card_spyglass_label: "마법 망원경",
  card_spyglass_footer: "미사용 시 +2",
  card_treasure_label: "보물",
  card_treasure_footer: "보물",
  card_unknown_footer: "알 수 없는 카드",

  // --- System phase (W9/W10) timed transitions ---
  sysphase_EndOfLeg_processing: "구간 결과 처리 중…",
  sysphase_EndOfLeg_success_title: "구간 성공",
  sysphase_EndOfLeg_success_body:
    "비행선이 다음 도시로 이동합니다. 탑승 중인 승객이 보물을 확보합니다. 선장이 교대됩니다.",
  sysphase_EndOfLeg_crash_title: "비행선 추락",
  sysphase_EndOfLeg_crash_body:
    "탑승 중이던 플레이어는 이번 여정 보상이 없습니다. 여정이 즉시 종료됩니다.",
  sysphase_EndOfJourney_processing: "새 모험 준비 중…",
  sysphase_EndOfJourney_title: "새 모험",
  sysphase_EndOfJourney_body:
    "전원 장비 카드 +1. 여행자 말이 비행선으로 복귀합니다. 도시 1로 초기화됩니다. 선장이 교대됩니다. (50점 이상이면 게임이 종료됩니다.)",
  sysphase_next: "잠시 후 다음 단계로 진행됩니다…",
};
